import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Megaphone, Mail, Phone, FileText, Search, Linkedin } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { useCurrentWorkspace } from '@/lib/workspace-context';
import { ROUTES } from '@/lib/routes';

type CampaignStatus = 'draft' | 'active' | 'paused' | 'archived' | 'goal_reached';

interface CampaignRow {
  id: string;
  name: string;
  code: string | null;
  status: CampaignStatus;
  channelsEnabled: string[];
  senderCompanyName: string | null;
  targetCountry: string | null;
  language: string | null;
  goalLimit: number | null;
  goalCount: number;
  goalAutoPaused: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CampaignsResponse {
  items: CampaignRow[];
  total: number;
  limit: number;
  offset: number;
}

const STATUS_LABEL: Record<CampaignStatus, { label: string; color: string }> = {
  draft: { label: 'Brouillon', color: 'bg-gray-100 text-gray-700' },
  active: { label: 'Active', color: 'bg-green-100 text-green-800' },
  paused: { label: 'En pause', color: 'bg-amber-100 text-amber-800' },
  archived: { label: 'Archivée', color: 'bg-gray-100 text-gray-500' },
  goal_reached: { label: 'Objectif atteint', color: 'bg-blue-100 text-blue-800' },
};

function ChannelBadge({ channel }: { channel: string }) {
  const map: Record<string, { Icon: typeof Mail; label: string; color: string }> = {
    mail: { Icon: Mail, label: 'Email', color: 'bg-purple-50 text-purple-700' },
    call: { Icon: Phone, label: 'Call', color: 'bg-blue-50 text-blue-700' },
    formfill: { Icon: FileText, label: 'Form-fill', color: 'bg-orange-50 text-orange-700' },
    linkedin_outreach: { Icon: Linkedin, label: 'Outreach', color: 'bg-brand-primary-light text-brand-primary' },
  };
  const def = map[channel];
  if (!def) return null;
  const { Icon, label, color } = def;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${color}`}>
      <Icon size={11} />
      {label}
    </span>
  );
}

export default function CampaignsListPage() {
  const { currentWorkspace } = useCurrentWorkspace();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CampaignStatus | ''>('');

  const wsId = currentWorkspace?.id;

  const { data, isLoading, error } = useQuery<CampaignsResponse>({
    queryKey: ['campaigns', wsId, statusFilter, search],
    queryFn: async () => {
      const params: Record<string, string | number> = { workspace_id: wsId! };
      if (statusFilter) params.status = statusFilter;
      if (search.trim()) params.q = search.trim();
      const { data } = await apiClient.get<CampaignsResponse>('/v1/campaigns', { params });
      return data;
    },
    enabled: !!wsId,
    refetchInterval: 30_000,
  });

  const empty = !isLoading && (data?.items.length ?? 0) === 0 && !search && !statusFilter;

  const items = useMemo(() => data?.items ?? [], [data]);

  if (!wsId) {
    return (
      <div className="p-8 text-gray-500">
        Sélectionne un workspace pour voir les campagnes.
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-brand-dark">Campagnes</h1>
          <p className="text-sm text-gray-500">
            {data?.total ?? 0} campagne{(data?.total ?? 0) > 1 ? 's' : ''} dans {currentWorkspace?.name}
          </p>
        </div>
        <Link
          to={ROUTES.CAMPAIGNS_NEW}
          className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-brand font-medium text-sm transition-colors"
        >
          <Plus size={16} />
          Nouvelle campagne
        </Link>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par nom..."
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-brand text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as CampaignStatus | '')}
          className="px-3 py-2 border border-gray-200 rounded-brand text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
        >
          <option value="">Tous les statuts</option>
          <option value="draft">Brouillon</option>
          <option value="active">Active</option>
          <option value="paused">En pause</option>
          <option value="goal_reached">Objectif atteint</option>
          <option value="archived">Archivée</option>
        </select>
      </div>

      {/* Empty state initial */}
      {empty && (
        <div className="bg-white border border-gray-200 rounded-brand p-12 text-center">
          <Megaphone size={40} className="mx-auto text-gray-300 mb-3" />
          <h3 className="text-lg font-medium text-gray-700 mb-1">Aucune campagne</h3>
          <p className="text-sm text-gray-500 mb-6">
            Lance ta première campagne multi-canal en quelques minutes.
          </p>
          <Link
            to={ROUTES.CAMPAIGNS_NEW}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-brand font-medium text-sm transition-colors"
          >
            <Plus size={16} />
            Créer ma première campagne
          </Link>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="bg-white border border-gray-200 rounded-brand p-8 text-center text-gray-400 text-sm">
          Chargement...
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-brand p-4 text-sm text-red-700">
          Erreur de chargement : {(error as Error).message}
        </div>
      )}

      {/* Table */}
      {!empty && !isLoading && items.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-brand overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Nom</th>
                <th className="text-left px-4 py-3 font-medium">Statut</th>
                <th className="text-left px-4 py-3 font-medium">Channels</th>
                <th className="text-left px-4 py-3 font-medium">Sender</th>
                <th className="text-left px-4 py-3 font-medium">Target</th>
                <th className="text-left px-4 py-3 font-medium">Objectif</th>
                <th className="text-left px-4 py-3 font-medium">Mise à jour</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => {
                const stat = STATUS_LABEL[c.status] ?? STATUS_LABEL.draft;
                return (
                  <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <Link
                        to={ROUTES.CAMPAIGN_DETAIL(c.id)}
                        className="text-brand-primary hover:underline font-medium"
                      >
                        {c.name}
                      </Link>
                      {c.code && <span className="ml-2 text-xs text-gray-400">{c.code}</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${stat.color}`}>
                        {stat.label}
                      </span>
                      {c.goalAutoPaused && (
                        <span className="ml-1 text-xs text-amber-700" title="Auto-pause objectif">⏸</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 flex-wrap">
                        {c.channelsEnabled.map((ch) => (
                          <ChannelBadge key={ch} channel={ch} />
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{c.senderCompanyName ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {c.targetCountry ?? '—'}
                      {c.language && <span className="text-xs text-gray-400 ml-1">{c.language}</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {c.goalLimit ? `${c.goalCount}/${c.goalLimit}` : '∞'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {new Date(c.updatedAt).toLocaleString('fr-FR', {
                        day: '2-digit', month: '2-digit', year: '2-digit',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Filtered empty */}
      {!isLoading && items.length === 0 && (search || statusFilter) && (
        <div className="bg-white border border-gray-200 rounded-brand p-8 text-center text-gray-500 text-sm">
          Aucune campagne ne correspond à ces filtres.
        </div>
      )}
    </div>
  );
}
